/**
 * Client wrappers for the ops-facing cash refund endpoints in ronin-api.
 * Same bearer-token pattern as modules/stripe_connect/api.ts.
 */

const API_BASE =
  (import.meta.env.VITE_RONIN_API_BASE as string | undefined) ||
  "https://api.roninpos.app";

function authHeaders(): HeadersInit {
  const token = localStorage.getItem("ron-auth");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
  } catch (err: any) {
    throw new Error(
      `Network error calling ${API_BASE}${path}: ${err?.message || err}. ` +
        `Check that ronin-api is running and CORS allows ${window.location.origin}.`
    );
  }
  if (!res.ok) {
    let message = `${res.status} ${res.statusText} from ${path}`;
    try {
      const data = await res.json();
      if (data?.error) message = `${res.status}: ${data.error}`;
      else if (data?.message) message = `${res.status}: ${data.message}`;
    } catch {
      if (res.status === 404) {
        message += " — endpoint not found. Is the cashRefund branch deployed?";
      }
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

/**
 * Deny a refund and put the balance back on the wristband. Refused once the
 * refund has gone into a payment file the bank has: cancelling then would
 * credit a wristband whose money is already on its way to someone's account.
 */
export function cancelCashRefund(guid: string, reason: string) {
  return postJson<{ guid: string; status: string; restored: boolean; amount: number }>(
    "/api/cashRefund/cancel",
    { guid, reason }
  );
}
